import React, {useEffect, useState} from 'react';
import {Box, Chip, Link, Paper, Stack, Typography} from '@mui/material';
import dayjs from 'dayjs';
import {getUsersByIds} from '../../../../firebase/firestore/users';
import PersonCell from '../../../collection/components/PersonCell';

function kindColor(kind) {
    if (kind === 'iir') return 'info';
    if (kind === 'cm') return 'secondary';
    return 'default';
}

function kindLabel(kind) {
    if (kind === 'iir') return 'IIR';
    if (kind === 'cm') return 'CM';
    return kind ? String(kind).toUpperCase() : 'Material';
}

/** Title for the clickable link — CM/IIR id lives in the subtitle. */
export function materialLinkLabel(material) {
    return material?.title || material?.displayId || '';
}

function formatAuthoredAt(value) {
    if (!value) {
        return null;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
}

/** CM/IIR authors are user ids (UUID). Display names fall back to plain text. */
function isResolvableUserId(value) {
    return typeof value === 'string'
        && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function Bullet() {
    return (
        <Typography variant="caption" color="text.secondary" aria-hidden>
            ·
        </Typography>
    );
}

function MaterialAuthor({userId}) {
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setProfile(null);
        if (!userId) {
            return undefined;
        }
        getUsersByIds([userId])
            .then((users) => {
                if (!cancelled) {
                    setProfile(users?.[0] || null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setProfile(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [userId]);

    return (
        <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                minWidth: 0,
                '& .MuiTypography-root, & a, & span': {
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                },
            }}
        >
            <PersonCell userId={userId} profile={profile} size="small"/>
        </Box>
    );
}

/**
 * Shared list row for collection materials (CMs and IIRs).
 * Title is the clickable link (opens the CM/IIR viewer via C2).
 * Subtitle: CM/IIR id · avatar first last · date.
 */
export default function CollectionMaterialRow({
    material,
    onOpen,
    showRelevance = false,
    showCited = false,
    showCitingFintels = false,
    onOpenReport,
}) {
    if (!material) {
        return null;
    }
    const clickable = typeof onOpen === 'function';
    const label = materialLinkLabel(material);
    const authoredAt = formatAuthoredAt(material.authoredAt);
    const displayId = material.displayId || null;
    const author = material.author || null;
    const authorUserId = isResolvableUserId(author) ? author.trim() : null;
    const authorLabel = !authorUserId && author ? author : null;
    const hasSubtitle = Boolean(displayId || authorUserId || authorLabel || authoredAt);

    return (
        <Paper variant="outlined" sx={{p: 1.5, borderRadius: 1}}>
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                        size="small"
                        label={kindLabel(material.materialKind)}
                        color={kindColor(material.materialKind)}
                        sx={{height: 20, fontSize: '0.65rem'}}
                    />
                    {clickable ? (
                        <Link
                            component="button"
                            variant="subtitle2"
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpen(material);
                            }}
                            sx={{
                                fontWeight: 700,
                                textAlign: 'left',
                                minWidth: 0,
                            }}
                        >
                            {label}
                        </Link>
                    ) : (
                        <Typography variant="subtitle2" fontWeight={700}>
                            {label}
                        </Typography>
                    )}
                    {showRelevance && material.relevancePct != null && (
                        <Chip
                            size="small"
                            variant="outlined"
                            color="primary"
                            label={`${Math.round(material.relevancePct)}% relevant`}
                            sx={{height: 20, fontSize: '0.65rem'}}
                        />
                    )}
                    {showCited && (
                        <Chip
                            size="small"
                            label={material.cited ? 'Cited in FINTEL' : 'Not cited'}
                            color={material.cited ? 'success' : 'default'}
                            variant={material.cited ? 'filled' : 'outlined'}
                            sx={{height: 20, fontSize: '0.65rem'}}
                        />
                    )}
                </Stack>
                {hasSubtitle && (
                    <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{minWidth: 0}}
                    >
                        {displayId && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontFamily="Roboto Mono, monospace"
                                sx={{wordBreak: 'break-all'}}
                            >
                                {displayId}
                            </Typography>
                        )}
                        {displayId && (authorUserId || authorLabel) && <Bullet/>}
                        {authorUserId ? (
                            <MaterialAuthor userId={authorUserId}/>
                        ) : authorLabel ? (
                            <Typography variant="caption" color="text.secondary">
                                {authorLabel}
                            </Typography>
                        ) : null}
                        {(displayId || authorUserId || authorLabel) && authoredAt && <Bullet/>}
                        {authoredAt && (
                            <Typography variant="caption" color="text.secondary">
                                {authoredAt}
                            </Typography>
                        )}
                    </Stack>
                )}
                {showCitingFintels && material.citingFintels?.length > 0 && (
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 0.25}}>
                            Citing FINTELs
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {material.citingFintels.map((f) => (
                                onOpenReport ? (
                                    <Link
                                        key={f.reportId}
                                        component="button"
                                        variant="caption"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenReport(f.reportId);
                                        }}
                                        sx={{fontWeight: 600}}
                                    >
                                        {f.title || f.reportId}
                                    </Link>
                                ) : (
                                    <Typography key={f.reportId} variant="caption" fontFamily="Roboto Mono, monospace">
                                        {f.title || f.reportId}
                                    </Typography>
                                )
                            ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
}
